import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  forwardRef,
  inject,
  signal,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NzInputDirective } from 'ng-zorro-antd/input';
import { NGXLogger } from 'ngx-logger';

export interface PlaceAutocompleteResult {
  place_id: string;
  name: string;
  formatted_address: string;
  geometry: {
    location: {
      lat(): number;
      lng(): number;
    };
  };
  address_components?: Array<{
    long_name: string;
    short_name: string;
    types: string[];
  }>;
  photos?: Array<{
    getUrl(options: { maxWidth: number; maxHeight: number }): string;
  }>;
}

@Component({
  selector: 'buspro-admin-place-autocomplete',
  standalone: true,
  imports: [CommonModule, NzInputDirective],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PlaceAutocompleteComponent),
      multi: true,
    },
  ],
  template: `
    <div class="relative w-full">
      <input
        #inputRef
        nz-input
        [placeholder]="placeholder"
        [disabled]="disabled"
        [value]="inputValue()"
        (input)="onInput($event)"
        (focus)="onFocus()"
        (blur)="onBlur()"
        (keydown)="onKeyDown($event)"
        class="w-full"
        autocomplete="off"
      />
      
      <!-- Loading indicator -->
      <div
        *ngIf="isLoading()"
        class="absolute right-3 top-1/2 transform -translate-y-1/2"
      >
        <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
      </div>
      
      <!-- Selection processing indicator -->
      <div
        *ngIf="isLoading()"
        class="absolute left-3 top-1/2 transform -translate-y-1/2"
      >
        <!-- <span class="text-blue-600 text-sm font-medium">Processing...</span> -->
      </div>
      
      <!-- Clear button -->
      <button
        *ngIf="inputValue() && !isLoading()"
        type="button"
        (click)="clearInput()"
        class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
      >
        <span class="fe fe-x"></span>
      </button>
      
      <!-- Suggestions dropdown -->
      <div
        *ngIf="showSuggestions() && suggestions().length > 0"
        class="absolute z-50 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto mt-1"
      >
        <div
          *ngFor="let suggestion of suggestions(); trackBy: trackByPlaceId; let i = index"
          (click)="selectSuggestion(suggestion)"
          (mousedown)="onMouseDownSuggestion(suggestion, $event)"
          (touchstart)="onTouchStartSuggestion(suggestion, $event)"
          (dblclick)="onDoubleClickSuggestion(suggestion, $event)"
          (mouseenter)="setHighlightedIndex(i)"
          [class.bg-blue-50]="highlightedIndex() === i"
          [class.bg-blue-100]="highlightedIndex() === i"
          class="px-4 py-3 hover:bg-gray-100 cursor-pointer border-b border-gray-200 last:border-b-0 transition-colors duration-150 select-none"
          title="Click or double-click to select"
        >
          <div class="font-medium text-gray-900 flex items-center">
            <span class="fe fe-map-pin mr-2 text-blue-500"></span>
            {{ suggestion.name }}
          </div>
          <div class="text-sm text-gray-600 ml-6">{{ suggestion.formatted_address }}</div>
          <!-- <div class="text-xs text-gray-400 ml-6 mt-1">Click or double-click to select</div> -->
        </div>
      </div>
      
      <!-- No results message -->
      <div
        *ngIf="showSuggestions() && suggestions().length === 0 && inputValue().length > 0 && !isLoading()"
        class="absolute z-50 w-full bg-white border border-gray-300 rounded-md shadow-lg mt-1 px-4 py-3"
      >
        <div class="text-gray-500 text-sm">No places found. Try a different search term.</div>
      </div>
    </div>
  `,
  styles: [
    `
    :host {
      position: relative;
      display: block;
    }
  `,
  ],
})
export class PlaceAutocompleteComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() placeholder = 'Search for places...';
  @Input() disabled = false;
  @Input() options: google.maps.places.AutocompleteOptions = {
    // types: ['establishment'],
    // componentRestrictions: { country: [] },
    componentRestrictions: { country: ['GH', 'UG'] },
    strictBounds: true,
  };

  @Output() placeSelected = new EventEmitter<PlaceAutocompleteResult>();
  @Output() inputChanged = new EventEmitter<string>();

  @ViewChild('inputRef', { static: true }) inputRef!: ElementRef<HTMLInputElement>;

  private logger = inject(NGXLogger);
  private autocompleteService?: google.maps.places.AutocompleteService;
  private placesService?: google.maps.places.PlacesService;
  private searchTimeout?: number;

  inputValue = signal('');
  suggestions = signal<PlaceAutocompleteResult[]>([]);
  showSuggestions = signal(false);
  isLoading = signal(false);
  highlightedIndex = signal(-1);

  private onChange = (_value: any) => {
    // Overwritten by registerOnChange (ControlValueAccessor)
  };
  private onTouched = () => {
    // Overwritten by registerOnTouched (ControlValueAccessor)
  };
  private isInitialized = false;
  private mouseDownSuggestion: PlaceAutocompleteResult | null = null;

  ngOnInit(): void {
    this.logger.log('PlaceAutocompleteComponent initialized');
    this.initializeGoogleMaps();
  }

  ngOnDestroy(): void {
    if (this.searchTimeout) {
      window.clearTimeout(this.searchTimeout);
    }
  }

  private async initializeGoogleMaps(): Promise<void> {
    try {
      // Wait for Google Maps to be loaded
      await this.waitForGoogleMaps();

      // Initialize services
      this.autocompleteService = new google.maps.places.AutocompleteService();
      this.placesService = new google.maps.places.PlacesService(document.createElement('div') as any);
      this.isInitialized = true;

      this.logger.log('Google Maps Places services initialized successfully');
    } catch (error) {
      this.logger.error('Failed to initialize Google Maps Places services', error);
      this.isInitialized = false;
    }
  }

  private waitForGoogleMaps(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (typeof google !== 'undefined' && google.maps && google.maps.places) {
        this.logger.log('Google Maps Places API already loaded');
        resolve();
        return;
      }

      this.logger.log('Waiting for Google Maps Places API to load...');
      let attempts = 0;
      const maxAttempts = 100; // 10 seconds (increased timeout)

      const checkInterval = setInterval(() => {
        attempts++;

        // Check for Google Maps API availability
        if (typeof google !== 'undefined' && google.maps) {
          // Check specifically for places library
          if (google.maps.places) {
            clearInterval(checkInterval);
            this.logger.log('Google Maps Places API loaded successfully');
            resolve();
            return;
          } else {
            this.logger.warn(`Google Maps loaded but places library missing (attempt ${attempts}/${maxAttempts})`);
          }
        }

        if (attempts >= maxAttempts) {
          clearInterval(checkInterval);
          const errorMsg =
            'Google Maps Places API not loaded after maximum attempts. Check if libraries=["places", "geometry"] are included in the Google Maps script.';
          this.logger.error(errorMsg);
          reject(new Error(errorMsg));
        }
      }, 100);
    });
  }

  onInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const value = target.value;

    this.inputValue.set(value);
    this.onChange(value);
    this.inputChanged.emit(value);

    // Clear previous timeout
    if (this.searchTimeout) {
      window.clearTimeout(this.searchTimeout);
    }

    if (value.length > 0) {
      this.isLoading.set(true);
      // Use shorter debounce for single characters, longer for longer strings
      const debounceTime = value.length === 1 ? 150 : 300;
      this.searchTimeout = window.setTimeout(() => {
        this.searchPlaces(value);
      }, debounceTime);
    } else {
      this.suggestions.set([]);
      this.showSuggestions.set(false);
      this.isLoading.set(false);
    }
  }

  private isComponentReady(): boolean {
    return this.isInitialized && !!this.autocompleteService && !!this.placesService;
  }

  // Public method to manually trigger search (useful for debugging)
  public triggerSearch(query: string): void {
    this.logger.log('Manually triggering search for:', query);
    this.searchPlaces(query);
  }

  onFocus(): void {
    if (this.suggestions().length > 0) {
      this.showSuggestions.set(true);
    }
  }

  onBlur(): void {
    // Delay hiding suggestions to allow clicking on them
    setTimeout(() => {
      this.showSuggestions.set(false);
      this.highlightedIndex.set(-1);
      this.onTouched();
    }, 300); // Increased delay to ensure click events can fire
  }

  onKeyDown(event: KeyboardEvent): void {
    const suggestions = this.suggestions();
    const highlighted = this.highlightedIndex();

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.highlightedIndex.set(Math.min(highlighted + 1, suggestions.length - 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.highlightedIndex.set(Math.max(highlighted - 1, -1));
        break;
      case 'Enter':
        event.preventDefault();
        if (highlighted >= 0 && suggestions[highlighted]) {
          this.logger.log('⌨️ Keyboard selection (Enter key):', suggestions[highlighted].name);
          this.selectSuggestion(suggestions[highlighted]);
        }
        break;
      case 'Escape':
        this.showSuggestions.set(false);
        this.highlightedIndex.set(-1);
        break;
    }
  }

  setHighlightedIndex(index: number): void {
    this.highlightedIndex.set(index);
  }

  clearInput(): void {
    this.inputValue.set('');
    this.onChange('');
    this.inputChanged.emit('');
    this.suggestions.set([]);
    this.showSuggestions.set(false);
    this.highlightedIndex.set(-1);
    this.placeSelected.emit(null as any);
  }

  private searchPlaces(query: string): void {
    if (!this.isComponentReady()) {
      this.logger.warn('Component not ready for search, attempting to reinitialize...');
      this.isLoading.set(false);
      // Try to reinitialize
      this.initializeGoogleMaps();
      return;
    }

    // Only search if query has at least 1 character
    if (query.length < 1) {
      this.suggestions.set([]);
      this.showSuggestions.set(false);
      this.isLoading.set(false);
      return;
    }

    this.logger.log('Searching places for query:', query);
    this.autocompleteService!.getPlacePredictions(
      {
        input: query,
        ...this.options,
      },
      (predictions, status) => {
        this.isLoading.set(false);
        this.logger.log('Search results:', { predictions, status });

        if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
          const mappedPredictions = predictions.map((prediction) => ({
            place_id: prediction.place_id,
            name: prediction.structured_formatting?.main_text || prediction.description,
            formatted_address: prediction.description,
            geometry: {
              location: {
                lat: () => 0,
                lng: () => 0,
              },
            },
          }));

          this.suggestions.set(mappedPredictions);
          this.showSuggestions.set(true);
          this.highlightedIndex.set(-1);
          this.logger.log('Suggestions updated:', mappedPredictions.length);
        } else {
          this.logger.warn('Places search failed', status);
          this.suggestions.set([]);
          this.showSuggestions.set(false);
        }
      },
    );
  }

  selectSuggestion(suggestion: PlaceAutocompleteResult): void {
    this.logger.log('🖱️ selectSuggestion called for:', suggestion.name);

    // Clear the mouse down flag since click event fired
    if (this.mouseDownSuggestion === suggestion) {
      this.mouseDownSuggestion = null;
    }

    // Prevent double-selection if already processing
    if (this.isLoading()) {
      this.logger.log('⏳ Already processing a selection, ignoring duplicate');
      return;
    }

    this.logger.log('🎯 Suggestion selected:', suggestion);
    this.logger.log('🎯 Suggestion place_id:', suggestion.place_id);

    // Set loading state to prevent double-clicks
    this.isLoading.set(true);

    // Update input value immediately for better UX
    this.inputValue.set(suggestion.name);
    this.onChange(suggestion.name);
    this.showSuggestions.set(false);
    this.highlightedIndex.set(-1);

    // Get detailed place information
    this.logger.log('🎯 Getting place details for:', suggestion.place_id);
    this.getPlaceDetails(suggestion.place_id);
  }

  // Handle mouse down to ensure click works properly
  onMouseDownSuggestion(suggestion: PlaceAutocompleteResult, _event: Event): void {
    this.logger.log('🖱️ Mouse down on suggestion:', suggestion.name);
    // Don't prevent default to allow normal click behavior
    // This helps ensure the click event fires properly

    // Set a flag to track if this was a mouse down event
    this.mouseDownSuggestion = suggestion;

    // Set a fallback timeout in case click doesn't fire
    setTimeout(() => {
      if (this.mouseDownSuggestion === suggestion) {
        this.logger.log('🔄 Fallback: Click event did not fire, triggering selection via mouse down');
        this.selectSuggestion(suggestion);
        this.mouseDownSuggestion = null;
      }
    }, 150); // 150ms should be enough for click to fire, but not too long for UX
  }

  // Handle touch start for mobile devices
  onTouchStartSuggestion(suggestion: PlaceAutocompleteResult, _event: Event): void {
    this.logger.log('👆 Touch start on suggestion:', suggestion.name);
    // Don't prevent default to allow normal touch behavior
    // This helps ensure the click event fires properly on mobile
  }

  // Handle double-click specifically for better UX
  onDoubleClickSuggestion(suggestion: PlaceAutocompleteResult, event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    this.logger.log('🖱️ Double-click detected on suggestion:', suggestion.name);
    this.selectSuggestion(suggestion);
  }

  // Method to ensure component is ready and reinitialize if needed
  private ensureComponentReady(): boolean {
    if (!this.isComponentReady()) {
      this.logger.warn('⚠️ Component not ready, attempting to reinitialize...');
      this.initializeGoogleMaps();
      return false;
    }
    return true;
  }

  private getPlaceDetails(placeId: string): void {
    if (!this.ensureComponentReady()) {
      // Reset loading state and emit fallback
      this.isLoading.set(false);
      this.emitFallbackResult(placeId);
      return;
    }

    this.logger.log('🔍 Getting place details for placeId:', placeId);

    // Set a timeout to prevent hanging
    const timeoutId = setTimeout(() => {
      this.logger.warn('⏰ Place details request timed out, using fallback');
      this.isLoading.set(false);
      this.emitFallbackResult(placeId);
    }, 10000); // 10 second timeout

    this.placesService!.getDetails(
      {
        placeId: placeId,
        fields: ['name', 'formatted_address', 'geometry', 'address_components', 'photos'],
      },
      (place, status) => {
        clearTimeout(timeoutId); // Clear timeout since we got a response
        this.isLoading.set(false);
        this.logger.log('🔍 Place details response:', { place, status });

        if (status === google.maps.places.PlacesServiceStatus.OK && place) {
          const result: PlaceAutocompleteResult = {
            place_id: place.place_id || '',
            name: place.name || '',
            formatted_address: place.formatted_address || '',
            geometry: {
              location: {
                lat: () => place.geometry?.location?.lat() || 0,
                lng: () => place.geometry?.location?.lng() || 0,
              },
            },
            address_components: place.address_components,
            photos: place.photos,
          };

          this.logger.log('🎯 Emitting place selected:', result);
          this.logger.log('🎯 Place selected event emitted successfully');
          this.placeSelected.emit(result);
        } else {
          this.logger.error('❌ Failed to get place details', status);
          // Try to emit a basic result with the information we have
          this.emitFallbackResult(placeId);
        }
      },
    );
  }

  trackByPlaceId(_index: number, suggestion: PlaceAutocompleteResult): string {
    return suggestion.place_id;
  }

  // Fallback method to emit basic result when Google Places API fails
  private emitFallbackResult(placeId: string): void {
    this.logger.log('🔄 Emitting fallback result for placeId:', placeId);

    const fallbackResult: PlaceAutocompleteResult = {
      place_id: placeId,
      name: this.inputValue(), // Use the input value as name
      formatted_address: this.inputValue(), // Use the input value as address
      geometry: {
        location: {
          lat: () => 0, // Default coordinates
          lng: () => 0,
        },
      },
      address_components: [],
      photos: [],
    };

    this.logger.log('🔄 Emitting fallback result:', fallbackResult);
    this.placeSelected.emit(fallbackResult);
  }

  // ControlValueAccessor implementation
  writeValue(value: any): void {
    this.inputValue.set(value || '');
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
